import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Scan, Eye, Link2, Trash2, Edit, AlertCircle, CheckCircle, X, Lock, Shield } from 'lucide-react';
import { MicrochipData } from './MicrochipLinking';

interface MicrochipManagementProps {
  petName: string;
  chipData: MicrochipData & { linkedDate: string };
  onUpdate: (chipData: Partial<MicrochipData>) => void;
  onUnlink: () => void;
  onClose: () => void;
}

export function MicrochipManagement({ petName, chipData, onUpdate, onUnlink, onClose }: MicrochipManagementProps) {
  const { t } = useTranslation();
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [editingVisibility, setEditingVisibility] = useState(false);
  const [tempVisibility, setTempVisibility] = useState(chipData.visibility);
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setIsAnimated(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const maskChipNumber = (number: string): string => {
    if (number.length <= 4) return number;
    const visible = number.slice(-4);
    const masked = '•'.repeat(number.length - 4);
    return masked + visible;
  };

  const handleSaveVisibility = () => {
    onUpdate({ visibility: tempVisibility });
    setEditingVisibility(false);
  };

  const handleToggleDID = () => {
    onUpdate({ linkToDID: !chipData.linkToDID });
  };

  const handleUnlink = () => {
    onUnlink();
    setShowUnlinkConfirm(false);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto overscroll-contain rounded-3xl border border-gray-200/80 transition-all duration-200 ease-out ${
          isAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.18), 0 12px 24px -8px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04), 0 0 40px -10px rgba(99,102,241,0.15)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Scan className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                {t('microchipManagement.title')}
              </h2>
              <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                {t('microchipManagement.forPet', { petName })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" style={{ color: '#464452' }} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Microchip Details Card */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                {t('petOverview.microchipCard.microchipLinked')}
              </h3>
            </div>

            <div className="space-y-4">
              {/* Chip Number */}
              <div>
                <div className="text-sm mb-1" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {t('petOverview.microchipCard.microchipNumber')}
                </div>
                <div className="font-mono bg-white px-4 py-3 rounded-lg" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '18px' }}>
                  {maskChipNumber(chipData.chipNumber)}
                </div>
              </div>

              {/* Standard */}
              {chipData.standard && (
                <div>
                  <div className="text-sm mb-1" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    {t('microchipManagement.microchipStandard')}
                  </div>
                  <div style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                    {chipData.standard}
                  </div>
                </div>
              )}

              {/* Implant Date */}
              {chipData.implantDate && (
                <div>
                  <div className="text-sm mb-1" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    {t('microchipManagement.implantDate')}
                  </div>
                  <div style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                    {new Date(chipData.implantDate).toLocaleDateString(undefined, { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
              )}

              {/* Implant Location */}
              {chipData.implantLocation && (
                <div>
                  <div className="text-sm mb-1" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    {t('microchipManagement.implantLocation')}
                  </div>
                  <div style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                    {chipData.implantLocation}
                  </div>
                </div>
              )}

              {/* Linked Date */}
              <div>
                <div className="text-sm mb-1" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {t('microchipManagement.linkedOnPetso')}
                </div>
                <div style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
                  {new Date(chipData.linkedDate).toLocaleDateString(undefined, { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* DID Link Toggle */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <Link2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                    {t('microchipManagement.linkToPetDid')}
                  </h4>
                  <p className="text-sm mb-2" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    {chipData.linkToDID 
                      ? t('microchipManagement.linkToDidDescOn')
                      : t('microchipManagement.linkToDidDescOff')
                    }
                  </p>
                  {chipData.linkToDID && (
                    <div className="flex items-center gap-2 text-xs text-green-600">
                      <CheckCircle className="w-3 h-3" />
                      <span>{t('microchipManagement.encryptedReferenceActive')}</span>
                    </div>
                  )}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={chipData.linkToDID}
                  onChange={handleToggleDID}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-purple-600"></div>
              </label>
            </div>
          </div>

          {/* Visibility Settings */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                  {t('microchipManagement.privacyVisibility')}
                </h4>
              </div>
              {!editingVisibility && (
                <button
                  onClick={() => {
                    setEditingVisibility(true);
                    setTempVisibility(chipData.visibility);
                  }}
                  className="px-3 py-1 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-1 text-sm"
                  style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <Edit className="w-4 h-4" />
                  {t('common.edit')}
                </button>
              )}
            </div>

            {!editingVisibility ? (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  {chipData.visibility === 'only-me' && <Lock className="w-4 h-4 text-blue-600" />}
                  {chipData.visibility === 'professionals' && <Eye className="w-4 h-4 text-purple-600" />}
                  {chipData.visibility === 'emergency' && <AlertCircle className="w-4 h-4 text-red-600" />}
                  <span className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                    {chipData.visibility === 'only-me' && t('microchipManagement.onlyMeLabel')}
                    {chipData.visibility === 'professionals' && t('microchipManagement.professionalsLabel')}
                    {chipData.visibility === 'emergency' && t('microchipManagement.emergencyLabel')}
                  </span>
                </div>
                <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {chipData.visibility === 'only-me' && t('microchipManagement.onlyMeDesc')}
                  {chipData.visibility === 'professionals' && t('microchipManagement.professionalsDesc')}
                  {chipData.visibility === 'emergency' && t('microchipManagement.emergencyDesc')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  tempVisibility === 'only-me'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="visibility"
                      value="only-me"
                      checked={tempVisibility === 'only-me'}
                      onChange={(e) => setTempVisibility(e.target.value as any)}
                      className="mt-1 w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-sm" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                          {t('microchipManagement.onlyMeLabel')}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('microchipManagement.completelyPrivate')}
                      </p>
                    </div>
                  </div>
                </label>

                <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  tempVisibility === 'professionals'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="visibility"
                      value="professionals"
                      checked={tempVisibility === 'professionals'}
                      onChange={(e) => setTempVisibility(e.target.value as any)}
                      className="mt-1 w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Eye className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-sm" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                          {t('microchipManagement.professionalsLabel')}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('microchipManagement.sharedDuringAppointments')}
                      </p>
                    </div>
                  </div>
                </label>

                <label className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  tempVisibility === 'emergency'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="radio"
                      name="visibility"
                      value="emergency"
                      checked={tempVisibility === 'emergency'}
                      onChange={(e) => setTempVisibility(e.target.value as any)}
                      className="mt-1 w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-sm" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                          {t('microchipManagement.emergencyLabel')}
                        </span>
                      </div>
                      <p className="text-xs" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('microchipManagement.forLostPetRecovery')}
                      </p>
                    </div>
                  </div>
                </label>

                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingVisibility(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                    style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('microchipManagement.cancel')}
                  </button>
                  <button
                    onClick={handleSaveVisibility}
                    className="flex-1 px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity text-sm"
                    style={{ background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('microchipManagement.saveChanges')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Important Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                <p className="mb-2">
                  <strong style={{ color: '#2D2F7E' }}>{t('microchipManagement.privacyNoticeTitle')}</strong> {t('microchipManagement.privacyNoticeBody1')}
                </p>
                <p>
                  {t('microchipManagement.privacyNoticeBody2')}
                </p>
              </div>
            </div>
          </div>

          {/* Unlink Section */}
          <div className="border-t-2 border-gray-200 pt-6">
            <h4 className="font-semibold mb-3" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
              {t('microchipManagement.unlinkMicrochip')}
            </h4>
            
            {!showUnlinkConfirm ? (
              <button
                onClick={() => setShowUnlinkConfirm(true)}
                className="w-full px-4 py-3 rounded-xl border-2 border-red-200 bg-red-50 hover:bg-red-100 transition-colors flex items-center justify-center gap-2 text-red-600"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                <Trash2 className="w-5 h-5" />
                {t('microchipManagement.removeMicrochipRef')}
              </button>
            ) : (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                      {t('microchipManagement.unlinkConfirmSure')}
                    </h5>
                    <p className="text-sm mb-2" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                      {t('microchipManagement.unlinkConfirmBodyFull')}
                    </p>
                    <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                      {t('microchipManagement.unlinkNote')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowUnlinkConfirm(false)}
                    className="flex-1 px-4 py-2 rounded-lg border-2 border-gray-300 hover:bg-gray-50 transition-colors"
                    style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('microchipManagement.cancel')}
                  </button>
                  <button
                    onClick={handleUnlink}
                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {t('microchipManagement.removeMicrochipButton')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
